import React from 'react';
import './MultiSelectFilter.css';

/** Properties for the MultiSelectFilter component. */
interface MultiSelectFilterProps {
  /** The label displayed for the filter. */
  label: string;
  /** The available options to choose from. */
  options: string[];
  /** The currently selected option values. */
  selected: string[];
  /** Called when the selection changes. */
  onChange: (selected: string[]) => void;
}

/**
 * An inline multi-select filter for table column headers.
 * Click the label to open a dropdown; selected items appear as removable pills.
 */
export const MultiSelectFilter: React.FC<MultiSelectFilterProps> = ({ label, options, selected, onChange }) => {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSelect = (value: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!selected.includes(value)) {
      onChange([...selected, value]);
    }
    setOpen(false);
  };

  const handleRemove = (value: string, e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(selected.filter(v => v !== value));
  };

  // Filter out already-selected options from the dropdown
  const availableOptions = options.filter(o => !selected.includes(o));

  return (
    <div ref={ref} className="MultiSelectFilter" style={{ position: 'relative' }}>
      <div className="MultiSelectFilter__Row">
        {/* Clickable header */}
        <span
          onClick={(e) => { e.stopPropagation(); setOpen(o => !o); }}
          className={`MultiSelectFilter__Header ${selected.length > 0 ? 'MultiSelectFilter__Header--active' : ''}`}
          title={`Filter by ${label.toLowerCase()}`}
        >
          {label}
          <i className="material-icons" style={{ fontSize: 16, verticalAlign: 'middle', marginLeft: 2 }}>
            {open ? 'arrow_drop_up' : 'arrow_drop_down'}
          </i>
        </span>

        {/* Pills inline to the right, wrapping below */}
        {selected.length > 0 && (
          <div className="MultiSelectFilter__Pills">
            {selected.map(v => (
              <span key={v} className="MultiSelectFilter__Pill">
                {v}
                <i className="material-icons MultiSelectFilter__PillClose" onClick={(e) => handleRemove(v, e)}>close</i>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Dropdown */}
      {open && (
        <ul className="MultiSelectFilter__Dropdown" onMouseDown={(e) => e.stopPropagation()}>
          {availableOptions.length === 0 && (
            <li className="MultiSelectFilter__Empty">No more options</li>
          )}
          {availableOptions.map(o => (
            <li key={o} onClick={(e) => handleSelect(o, e)}>{o}</li>
          ))}
        </ul>
      )}
    </div>
  );
};
